import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { analytics } from '@/services/analytics'
import { logger } from '@/utils/logger'

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    // If the user navigated via browser back/forward, restore saved position
    if (savedPosition) {
      return savedPosition
    }
    // If navigating to a hash (anchor), scroll to it
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    // Otherwise, scroll to top
    return { top: 0, behavior: 'smooth' }
  },
  routes: [
    {
      path: '/',
      name: 'Home',
      component: () => import('@/views/HomePage.vue')
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'Register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/verify-email',
      name: 'VerifyEmail',
      component: () => import('@/views/VerifyEmailView.vue')
    },
    {
      path: '/unsubscribe',
      name: 'Unsubscribe',
      component: () => import('@/views/UnsubscribeView.vue')
    },
    {
      path: '/verify-phone',
      name: 'VerifyPhone',
      component: () => import('@/views/VerifyPhoneView.vue')
    },
    {
      path: '/auth/callback',
      name: 'AuthCallback',
      component: () => import('@/views/AuthCallbackView.vue')
    },
    {
      path: '/onboarding',
      name: 'Onboarding',
      component: () => import('@/views/OnboardingView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/portfolio',
      name: 'Portfolio',
      component: () => import('@/views/PortfolioView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/recommendations/discover',
      name: 'StockDiscovery',
      component: () => import('@/views/StockDiscoveryView.vue'),
      meta: { requiresAuth: true, requirePremium: true }
    },
    {
      path: '/analytics',
      name: 'Analytics',
      component: () => import('@/views/AnalyticsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/recommendations',
      name: 'Recommendations',
      component: () => import('@/views/RecommendationsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/watchlist',
      name: 'Watchlist',
      component: () => import('@/views/WatchlistView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/chat',
      name: 'Chat',
      component: () => import('@/views/FinancialChatView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings/api-keys',
      name: 'APIKeys',
      component: () => import('@/views/ApiKeysView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/alerts',
      name: 'Alerts',
      component: () => import('@/views/AlertsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/stocks/:symbol',
      name: 'StockDetail',
      component: () => import('@/views/StockDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/stocks',
      name: 'Stocks',
      component: () => import('@/views/StocksView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/pricing',
      name: 'Pricing',
      component: () => import('@/views/PricingView.vue')
    },
    {
      path: '/subscription/success',
      name: 'SubscriptionSuccess',
      component: () => import('@/views/SubscriptionSuccessView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/subscription/cancelled',
      name: 'SubscriptionCancelled',
      component: () => import('@/views/SubscriptionCancelledView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/transparency',
      name: 'Transparency',
      component: () => import('@/views/WalkForwardTransparencyView.vue')
    },
    {
      path: '/benchmarks',
      name: 'Benchmarks',
      component: () => import('@/views/BenchmarksView.vue')
    },
    {
      path: '/research/benchmarks',
      name: 'ComprehensiveBenchmarks',
      component: () => import('@/views/ComprehensiveBenchmarksView.vue')
    },
    {
      path: '/research/validation-report',
      name: 'ValidationReport',
      component: () => import('@/views/ValidationReportView.vue')
    },
    {
      path: '/terms/api',
      name: 'APITerms',
      component: () => import('@/views/ApiTermsView.vue')
    },
    {
      path: '/mcp',
      name: 'MCPLanding',
      component: () => import('@/views/McpLandingView.vue')
    },
    {
      path: '/prompts',
      name: 'PromptLibrary',
      component: () => import('@/views/PromptLibraryView.vue')
    },
    {
      path: '/transparency-realtime',
      name: 'TransparencyRealtime',
      component: () => import('@/views/TransparencyView.vue'),
      meta: { requiresTransparencyFlag: true }
      // Legacy real-time tracking page (admin-only feature flag)
    },
    {
      path: '/admin/analytics',
      name: 'AdminAnalytics',
      component: () => import('@/views/AdminAnalyticsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/ml-performance',
      name: 'AdminMLPerformance',
      component: () => import('@/views/AdminMLPerformanceView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/monitoring',
      name: 'ProductionMonitoring',
      component: () => import('@/views/ProductionMonitoringView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/mcp-analytics',
      name: 'MCPAnalytics',
      component: () => import('@/views/MCPAnalyticsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfService',
      component: () => import('@/views/TermsOfServiceView.vue')
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: () => import('@/views/PrivacyPolicyView.vue')
    },
    {
      path: '/cookie-policy',
      name: 'CookiePolicy',
      component: () => import('@/views/CookiePolicyView.vue')
    },
    {
      path: '/contact',
      name: 'Contact',
      component: () => import('@/views/ContactView.vue')
    },
    {
      path: '/dev/modal-preview',
      name: 'DevModalPreview',
      component: () => import('@/views/DevModalPreview.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue')
    },
  ],
})

// Helper function to check if user is admin
const isAdmin = (user: any): boolean => {
  if (!user) return false
  // Check actual isAdmin flag from database
  return user.isAdmin === true
}

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  
  // Initialize auth store if needed
  await authStore.initialize()

  const user = authStore.user
  const isAuthenticated = authStore.isAuthenticated

  // Check if transparency page is enabled (if route requires it)
  if (to.meta.requiresTransparencyFlag) {
    try {
      const { default: apiService } = await import('@/services/api')
      const response = await apiService.getPublicSystemSettings()
      if (!response.success || !response.data?.showTransparency) {
        // Transparency page is disabled, redirect to home
        next('/')
        return
      }
    } catch (error) {
      console.error('Failed to check transparency flag:', error)
      next('/')
      return
    }
  }

  // Public routes that don't require authentication or verification
  const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/verify-phone', '/auth/callback', '/terms-of-service', '/privacy-policy', '/cookie-policy', '/contact']
  const isPublicRoute = publicRoutes.includes(to.path)

  // Routes that don't require phone verification (but do require auth)
  const noPhoneVerificationRequired = ['/settings/api-keys', '/mcp', '/terms/api', '/transparency']
  const skipPhoneVerification = noPhoneVerificationRequired.includes(to.path)

  // If route requires authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    next('/login')
    return
  }

  // If authenticated user tries to access auth routes, redirect to appropriate page
  if (to.meta.requiresGuest && isAuthenticated) {
    // Check if user needs to complete verification
    if (user && !user.emailVerified) {
      next('/verify-email')
      return
    }
    if (user && !user.phoneVerified) {
      next('/verify-phone')
      return
    }
    next('/dashboard')
    return
  }

  // Check if authenticated user has completed all verifications
  if (isAuthenticated && !isPublicRoute && to.meta.requiresAuth) {
    logger.debug('🔒 [GUARD] Checking verification for:', to.path);
    logger.debug('🔒 [GUARD] Skip phone verification?', skipPhoneVerification);
    logger.debug('Current user before reload:', {
      id: authStore.user?.id,
      email: authStore.user?.email,
      phoneVerified: authStore.user?.phoneVerified,
      emailVerified: authStore.user?.emailVerified
    });
    
    // Reload user data to get fresh verification status
    await authStore.loadCurrentUser()

    // loadCurrentUser() may have called logout() if the token was rejected (401).
    // Re-check authentication state AFTER the reload — if the user was logged out,
    // redirect to login instead of proceeding (prevents blank page / 404 on refresh
    // after a backend redeploy that invalidates existing JWTs).
    if (!authStore.isAuthenticated) {
      next('/login')
      return
    }

    const freshUser = authStore.user
    
    logger.debug('User after reload:', {
      id: freshUser?.id,
      email: freshUser?.email,
      phoneVerified: freshUser?.phoneVerified,
      emailVerified: freshUser?.emailVerified,
      authProvider: freshUser?.authProvider
    });
    
    // Skip verification checks for admin users
    const userIsAdmin = isAdmin(freshUser);
    
    // Check email verification (for local auth users, skip for admins)
    if (!userIsAdmin && freshUser?.authProvider === 'local' && !freshUser.emailVerified) {
      console.warn('🔒 [GUARD] ❌ Access denied: Email verification required')
      next('/verify-email?message=Please verify your email to access your account')
      return
    }

    // Check phone verification (required for onboarding, but skip for API/MCP pages)
    if (!userIsAdmin && !skipPhoneVerification && freshUser && !freshUser.phoneVerified) {
      console.warn('🔒 [GUARD] ❌ Access denied: Phone verification required')
      next('/verify-phone?message=Please verify your phone number to access your account')
      return
    }
    
    logger.debug('All verifications passed, allowing access to:', to.path);
  }

  // Check admin access for admin routes
  if (to.meta.requiresAdmin) {
    if (!isAdmin(user)) {
      console.warn('Access denied: Admin privileges required')
      next('/dashboard')
      return
    }
  }

  next()
})

// Handle lazy-loaded chunk failures caused by a Vercel redeployment while the user
// is active. When a new deploy goes live the old hashed chunks (e.g.
// PortfolioView-83441603.js) are replaced with new ones. Any in-flight dynamic
// import for an old URL returns 404 and Vue Router throws a TypeError. Catching it
// here forces a hard navigation to the intended route so the browser fetches the
// latest deployment's assets automatically — users land on the right page with no
// visible disruption.
router.onError((err, to) => {
  const isChunkLoadError =
    err?.message?.includes('Failed to fetch dynamically imported module') ||
    err?.message?.includes('Importing a module script failed') ||
    err?.message?.includes('dynamically imported module') ||
    err?.message?.includes('Unable to preload CSS')
  if (isChunkLoadError) {
    // Guard against infinite reload loops (allow at most one reload per 10s)
    const lastReload = Number(sessionStorage.getItem('chunkReloadAt') || 0)
    if (Date.now() - lastReload > 10_000) {
      sessionStorage.setItem('chunkReloadAt', String(Date.now()))
      window.location.href = to.fullPath
    }
  }
})

// Track page views after navigation
router.afterEach((to) => {
  // Get page title from route meta or name
  const pageTitle = (to.meta.title as string) || to.name?.toString() || 'Unknown'
  
  analytics.trackPageView(to.path, pageTitle)
})

export default router